import type { FC, ReactNode } from 'react';
import { cn } from '../utils/cn';

/**
 * Плитка статистики на карточке приза и ряд из двух таких плиток.
 *
 * Сведено в #544. `DailyGiveawayCard` и `RaffleCard` рисовали одну и ту же вещь — две
 * равные плитки с [иконкой или аватаром] и парой строк, — но фон, скругление, падинги и
 * контейнер ряда у них разъехались без причины (`bg-white/10` против `bg-white/15`,
 * `rounded-lg` против `rounded-xl`, `px-2 py-1.5` против `px-3 py-2`, `flex` против `grid`).
 * Здесь оболочка одна на обе.
 *
 * **Типографики у плитки нет намеренно, и пропсов на кегль тоже.** Обе строки приходят
 * готовыми узлами от карточки, потому что порядок и кегль следуют содержимому, а не
 * оболочке: где значение — число, оно идёт крупным снизу под мелкой подписью; где значение —
 * имя человека или номер места, оно идёт заголовком сверху. Замер на 360px (#544) показал,
 * что сведение и этой части к одному виду стоило бы номера места: «Твоё место #4» при
 * `text-lg` обрезается до «Твоё место…».
 */

interface PrizeStatTileProps {
  /** Глиф или аватар слева. */
  icon?: ReactNode;
  /** Две строки плитки — метка и значение, в порядке, который задаёт карточка. */
  children: ReactNode;
  /** Есть обработчик — плитка становится кнопкой и получает отклик на нажатие. */
  onClick?: () => void;
  className?: string;
}

export const PrizeStatTile: FC<PrizeStatTileProps> = ({ icon, children, onClick, className }) => {
  const inner = (
    <>
      {icon}
      <div className="min-w-0 flex-1 leading-tight">{children}</div>
    </>
  );
  const shell = cn('flex min-w-0 items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-start', className);

  if (!onClick) {
    return <div className={shell}>{inner}</div>;
  }
  return (
    <button type="button" onClick={onClick} className={cn(shell, 'transition active:bg-white/25')}>
      {inner}
    </button>
  );
};

/** Ряд из двух равных плиток. Одна плитка на всю ширину — просто не оборачивать в ряд. */
export const PrizeStatTilePair: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('grid grid-cols-2 gap-2', className)}>{children}</div>
);
